import { DateTime } from "luxon"
import { Allergen } from "../../admin/products/productsTypes"
import { MonetaryAmount } from "../../../currency/currencyTypes"

export type ProductDescription = Readonly<{
    allergens?: Allergen[]
    marketingTag?: string
}>

export type ProductPhoto = Readonly<{
    uid: string
    url: string
}>

export type DishItem = Readonly<{
    name: string
    description?: string
    productDescription?: ProductDescription
    price?: MonetaryAmount
    productPhoto?: ProductPhoto
}>

export type Recurrence = Readonly<{
    days: DaysOfWeekType[]
    fromDate: string
    toDate: string
}>

export type DailyMenu = Readonly<{
    id: number
    type: DayMenuType
    publishDate: string
    isActive: boolean
    color: string
    recurrence?: Recurrence
    dishes: DishItem[]
    signageTypes?: SignageTypeList[]
}>

export type Week = Readonly<{
    startDate: string
    weekInYear: number
    displayWeek: string
    dailyMenus: DailyMenu[]
}>

export type MenuPlanningContent = Readonly<{
    title?: string
    logoUrl?: string
    menuQrUrl?: string
}>

export type MenuPlanningList = Readonly<{
    externalFeeds: [
        {
            type: "RSS"
            url: string
        },
        {
            type: "ATOM"
            url: string
        },
    ]
    content?: MenuPlanningContent
    weeks: Week[]
}>

export enum DayMenuType {
    DIGITAL_SIGNAGE = "DIGITAL_SIGNAGE",
    WEB_MENU = "WEB_MENU",
}

export enum SignageType {
    WEB_MENU = "WEB_MENU",
    BUFFET_SIGN = "BUFFET_SIGN",
    DAY_MENU_SIGN = "DAY_MENU_SIGN",
}
export type SignageTypeList = keyof typeof SignageType

export enum DaysOfWeek {
    MONDAY = "MONDAY",
    TUESDAY = "TUESDAY",
    WEDNESDAY = "WEDNESDAY",
    THURSDAY = "THURSDAY",
    FRIDAY = "FRIDAY",
    SATURDAY = "SATURDAY",
    SUNDAY = "SUNDAY",
}
export type DaysOfWeekType = keyof typeof DaysOfWeek

export type RecurrenceDay = Readonly<{
    days?: DaysOfWeekType[]
    fromDate?: DateTime
    toDate?: DateTime
}>

export type CreateUpdateDayMenuDish = Readonly<{
    name: string
    description?: string
    productDescription?: ProductDescription
    price?: number | undefined
    currency?: string
    productPhoto?: {
        uid?: string
        url: string
    }
}>

export type CreateUpdateDayMenuFormValues = Readonly<{
    publishDate: DateTime
    isActive: boolean
    color: {
        name: string
        code: string
    } | null
    signageTypes: SignageTypeList[]
    dishes: CreateUpdateDayMenuDish[]
    recurrence?: RecurrenceDay
}>
export type MenuDetails = Readonly<{
    id: number
    merchant: {
        id: number
        name: string
    }
    publishDate: string
    isActive: boolean
    color: string
    signageTypes: SignageTypeList[]
    recurrence?: Recurrence
    dishes: DishItem[]
}>

export enum BatteryLevel {
    UNDEFINED = "0",
    UNKNOWN = "1",
    GOOD = "2",
    POOR = "3",
    POOR_CHILLER = "4",
    REPLACE = "5",
    REPLACE_CHILLER = "6",
}

export enum DigitalSignageStatusCode {
    UNKNOWN = "-99",
    FAILED_CLEANSING = "-10",
    FAILED_SYSTEM_CONFIGURATION = "-2",
    FAILED_NETWORK_CONFIGURATION = "-1",
    NOT_CONFIGURED = "0",
    NETWORK_CONFIGURED = "1",
    FAILED_COMMUNICATION = "2",
    LOW_BATTERY = "3",
    CURRENT_PAGE_DESIGN_NOT_FOUND = "4",
    CURRENT_PAGE_PRODUCT_NOT_FOUND = "5",
    CURRENT_PAGE_FAILED_UPDATE = "6",
    ALTERNATE_PAGE_DESIGN_NOT_FOUND = "7",
    ALTERNATE_PAGE_PRODUCT_NOT_FOUND = "8",
    ALTERNATE_PAGE_FAILED_UPDATE = "9",
    OKAY = "10",
    FAILED_HEALTH_CHECK = "11",
    LOW_TEMPERATURE = "12",
    IMAGE_OVERWRITTEN = "20",
    PRECONFIGURED = "50",
    QUEUED = "99",
}

export type DigitalSignage = Readonly<{
    id: number
    serialNumber: string
    name: string
    lastUpdated: string
    batteryLevel: BatteryLevel
    statusCode: DigitalSignageStatusCode
    imageUrl?: string
}>

export type MerchantConfig = Readonly<{
    config: {
        merchantSettings: Array<MerchantSettings>
    }
}>

export type MerchantSettings = Readonly<{
    name: string
    value: string
}>
